import React, { useEffect, useState } from "react";
import { Wallet, Download } from "lucide-react";
import PeopleLayout from "../../components/PeopleLayout";
import {
  peopleApi,
  hasCapability,
  formatDetail,
} from "../../lib/api";

const CAP = "payroll.finalize";

export default function PeoplePayroll() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const allowed = hasCapability(CAP);

  useEffect(() => {
    if (!allowed) {
      setLoading(false);
      return;
    }
    peopleApi.get("/people/payroll/eligible")
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [allowed]);

  const exportJson = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payroll_eligible_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <PeopleLayout
      title="Payroll Eligibility"
      subtitle="Only ELIGIBLE records — the single source of payroll truth"
      actions={
        allowed && data && (
          <button
            data-testid="pe-payroll-export"
            onClick={exportJson}
            className="h-10 px-4 border border-neutral-700 hover:border-amber-400 hover:text-amber-300 font-mono text-xs uppercase tracking-wider inline-flex items-center gap-2"
          >
            <Download size={14} /> Export JSON
          </button>
        )
      }
    >
      {!allowed && (
        <div
          data-testid="pe-payroll-no-cap"
          className="border border-amber-500/40 bg-amber-500/10 p-6 font-mono text-xs text-amber-200"
        >
          Your role does not include <code>{CAP}</code>. Ask Control Room
          to grant the payroll-finalise capability.
        </div>
      )}

      {allowed && loading && (
        <div className="font-mono text-xs text-neutral-500">Loading…</div>
      )}

      {allowed && data && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <Stat label="ELIGIBLE" value={data.totals.ELIGIBLE} accent="text-amber-300" testid="pe-stat-eligible" />
            <Stat label="PENDING REVIEW" value={data.totals.PENDING_REVIEW} accent="text-neutral-300" testid="pe-stat-pending" />
            <Stat label="EXCLUDED" value={data.totals.EXCLUDED} accent="text-red-400" testid="pe-stat-excluded" />
          </div>

          <div className="mt-6 border border-neutral-800 bg-[#13161C]">
            <div className="px-5 py-3 border-b border-neutral-800 flex items-center justify-between">
              <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-neutral-400">
                Eligible records (rule {data.payroll_rule_version})
              </div>
              <div className="font-mono text-[11px] text-neutral-500">
                returned: {data.returned}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#181B22] text-neutral-500 font-mono text-[10px] uppercase tracking-[0.18em]">
                  <tr>
                    <th className="text-left px-4 py-2">Employee</th>
                    <th className="text-left px-4 py-2">Outlet</th>
                    <th className="text-left px-4 py-2">Check-in</th>
                    <th className="text-left px-4 py-2">Check-out</th>
                    <th className="text-left px-4 py-2">Source</th>
                    <th className="text-left px-4 py-2">Approved by</th>
                  </tr>
                </thead>
                <tbody>
                  {data.records.map((r) => (
                    <tr
                      key={r.id}
                      className="border-t border-neutral-800"
                      data-testid={`pe-payroll-row-${r.id}`}
                    >
                      <td className="px-4 py-2 font-mono text-xs text-neutral-200">
                        {r.employee_id}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-neutral-400">
                        {r.outlet_id}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-neutral-300">
                        {r.check_in_at_iso ? new Date(r.check_in_at_iso).toLocaleString("en-GB") : "—"}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-neutral-300">
                        {r.check_out_at_iso ? new Date(r.check_out_at_iso).toLocaleString("en-GB") : "—"}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-neutral-400">
                        {r.source || "—"}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-amber-300/80">
                        {r.approved_by_manager_id || "auto"}
                      </td>
                    </tr>
                  ))}
                  {data.records.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center font-mono text-xs text-neutral-500">
                        No eligible records yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] text-neutral-500">
            <Wallet size={12} /> Records here are the only ones that may reach a payroll integration.
          </div>
        </>
      )}

      {err && (
        <div className="mt-4 text-red-400 font-mono text-sm" data-testid="pe-payroll-error">
          {err}
        </div>
      )}
    </PeopleLayout>
  );
}

function Stat({ label, value, accent = "", testid }) {
  return (
    <div data-testid={testid} className="border border-neutral-800 bg-[#13161C] p-4">
      <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-neutral-500">{label}</div>
      <div className={`mt-2 font-heading text-3xl font-black tracking-tighter ${accent}`}>{value}</div>
    </div>
  );
}
